/**
 * Where a tag's history comes from, mirroring the proxy's history routing
 * (`tag/serving/history.go` `historyRoute`):
 *
 *   1. An external read-through historian wins — a PI Web API WebId sidecar
 *      or a Canary Views tag-name sidecar — and history is never copied into
 *      the internal historian, even when the sidecar is bound to an existing
 *      Ignition/OPC tag whose live reads stay put.
 *   2. Otherwise an enabled, non-quarantined registration is collected into
 *      the internal historian.
 *   3. Ignition-covered tags with gateway history enabled are also federated
 *      from the Ignition historian (it fills gaps, or serves alone when
 *      nothing is collected here).
 *   4. Anything else has no history provider.
 */
export type HistoryProvider = 'internal' | 'ignition' | 'pi' | 'canary'

export type HistorizationKind = 'internal' | 'external' | 'not_historized'

export interface HistorizationStatus {
  kind: HistorizationKind
  /** Providers that serve history for this tag, in query priority order. */
  providers: HistoryProvider[]
  /** Short badge text. */
  label: string
  /** One-sentence human explanation of why. */
  reason: string
}

export interface HistorizationInput {
  /** No registry row: the graph tag is not registered for collection. */
  graphOnly?: boolean
  /** Registry collection enabled (`tags.enabled`). */
  enabled: boolean
  acquisitionQuarantined?: boolean
  /** Registry protocol / source type, used to detect standalone PI / Canary tags. */
  registrationProtocol?: string
  sourceType?: string
  /** A PI WebId sidecar is attached (standalone or bound to an existing tag). */
  piHistory?: boolean
  /** A Canary Views tag-name sidecar is attached (standalone or bound to an existing tag). */
  canaryHistory?: boolean
  /** The tag's Ignition gateway historian is enabled for this tag. */
  gatewayHistoryEnabled?: boolean
}

export const HISTORY_PROVIDER_LABEL: Record<HistoryProvider, string> = {
  internal: 'Internal historian',
  ignition: 'Ignition historian',
  pi: 'PI historian',
  canary: 'Canary historian'
}

type ExternalHistorian = 'pi' | 'canary'

const EXTERNAL_HISTORIAN_NAME: Record<ExternalHistorian, string> = {
  pi: 'PI Web API',
  canary: 'Canary Views'
}

function standaloneExternalHistorian(input: HistorizationInput): ExternalHistorian | null {
  const protocol = input.registrationProtocol?.trim().toLowerCase()
  const sourceType = input.sourceType?.trim().toLowerCase()
  if (protocol === 'pi' || sourceType === 'pi') return 'pi'
  if (protocol === 'canary' || sourceType === 'canary') return 'canary'
  return null
}

/**
 * The external historian that serves this registration's history, or null
 * when history stays on the internal / Ignition stores. A standalone PI or
 * Canary registration has no other history source; a sidecar bound to an
 * existing tag routes history externally while live reads stay put.
 */
function externalHistorian(input: HistorizationInput): {
  provider: ExternalHistorian
  standalone: boolean
} | null {
  const standalone = standaloneExternalHistorian(input)
  if (standalone) return { provider: standalone, standalone: true }
  if (input.piHistory) return { provider: 'pi', standalone: false }
  if (input.canaryHistory) return { provider: 'canary', standalone: false }
  return null
}

/**
 * Whether new values are being collected into the internal historian right
 * now: registered, enabled and not quarantined. Trend verdicts are computed
 * from those collected readings, so this — not "has any history provider" —
 * decides whether a trend may be shown.
 */
export function isCollectingHistory(
  input: Pick<HistorizationInput, 'graphOnly' | 'enabled' | 'acquisitionQuarantined'>
): boolean {
  return !input.graphOnly && input.enabled && input.acquisitionQuarantined !== true
}

export function deriveHistorizationStatus(input: HistorizationInput): HistorizationStatus {
  const registered = !input.graphOnly
  const external = registered ? externalHistorian(input) : null

  if (external) {
    const name = EXTERNAL_HISTORIAN_NAME[external.provider]
    return {
      kind: 'external',
      providers: [external.provider],
      label: HISTORY_PROVIDER_LABEL[external.provider],
      reason: external.standalone
        ? `History is read directly from ${name}; values are not copied into the internal historian.`
        : `History is read from ${name}. Live values still come from this tag’s own source.`
    }
  }

  const quarantined = registered && input.acquisitionQuarantined === true
  const collecting = isCollectingHistory(input)
  const ignition = input.gatewayHistoryEnabled === true

  if (collecting) {
    return ignition
      ? {
          kind: 'internal',
          providers: ['internal', 'ignition'],
          label: 'Internal + Ignition',
          reason:
            'New values are collected into the internal historian; the Ignition historian fills gaps.'
        }
      : {
          kind: 'internal',
          providers: ['internal'],
          label: HISTORY_PROVIDER_LABEL.internal,
          reason: 'New values are collected into the internal historian.'
        }
  }

  const notCollectedReason = !registered
    ? 'This tag is not registered for collection'
    : quarantined
      ? 'This tag’s registration needs review, so nothing is collected'
      : 'History collection is disabled for this tag'

  if (ignition) {
    return {
      kind: 'external',
      providers: ['ignition'],
      label: HISTORY_PROVIDER_LABEL.ignition,
      reason: `${notCollectedReason}; history is read from the Ignition gateway historian.`
    }
  }

  return {
    kind: 'not_historized',
    providers: [],
    label: 'Not historized',
    reason: `${notCollectedReason}, and no external historian covers it.`
  }
}

/**
 * Why a row records no history, or undefined while it is collecting. Every
 * not-collecting tag shows the same "Disabled" chip; the tooltip says why.
 */
export function rowNotCollectedReason(tag: {
  state: string
  graphOnly?: boolean
  acquisitionQuarantined?: boolean
}): string | undefined {
  if (tag.graphOnly === true) {
    return 'History is disabled: this tag is not registered for collection. Register it to start recording values.'
  }
  if (tag.state === 'disabled') {
    return 'History is disabled and no new values are recorded. Live reads remain available.'
  }
  if (tag.acquisitionQuarantined === true) {
    return 'History is disabled: this tag’s registration needs review, so nothing is collected.'
  }
  return undefined
}
